import DOMPurify from 'dompurify'
import { LineDiff } from 'features/chatbot/components/conversationMessage/LineDiff'
import {
  CaseProposal,
  parseCaseProposalEntry,
  ProposableFieldPath,
  useCaseProposalAPI,
} from 'features/mdCases/caseProposalAPI'
import { useCaseUpdateHandlers } from 'features/mdCases/CaseUpdateContext'
import { useRolodexEntries } from 'features/rolodex'
import {
  Check,
  CircleAlert,
  CircleCheck,
  CircleX,
  Sparkles,
  X,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

// Match the labels the case page uses on its visible sections:
//   notes.working_notes        → "Investigation"  (MdCaseDetailsMainColumn:381)
//   notes.recommendation_draft → "Recommendation" (MdCaseDetailsMainColumn:440)
//   workflow.severity          → "Severity"       (MdCaseDetailsInspector:393)
//   meta.title                 → "Title"          (page header)
// Keeping these in sync avoids confusing users with one term in the
// suggestion card and another on the section they're editing.
const FIELD_LABELS: Record<ProposableFieldPath, string> = {
  'meta.title': 'Title',
  'workflow.severity': 'Severity',
  'notes.working_notes': 'Investigation',
  'notes.recommendation_draft': 'Recommendation',
}

const Card = styled.div<{ $status: CaseProposal['status'] }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.5rem 0;
  padding: 0.625rem 0.75rem;
  border: 1px solid
    ${({ theme, $status }) =>
      $status === 'accepted'
        ? '#10b981'
        : $status === 'rejected'
          ? theme.forecast.border
          : theme.forecast.border2};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.background2_5};
  opacity: ${({ $status }) =>
    $status === 'accepted' || $status === 'rejected' ? 0.75 : 1};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 600;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-weight: 400;
`

const Pill = styled.span<{ $variant: 'old' | 'new' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'new'
        ? 'rgba(16, 185, 129, 0.4)'
        : 'rgba(239, 68, 68, 0.4)'};
  color: ${({ $variant }) => ($variant === 'new' ? '#10b981' : '#ef4444')};
  background: ${({ $variant }) =>
    $variant === 'new'
      ? 'rgba(16, 185, 129, 0.08)'
      : 'rgba(239, 68, 68, 0.08)'};
`

const PillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
`

const Arrow = styled.span`
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-size: 0.75rem;
`

const Rationale = styled.div`
  font-size: 0.7rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Actions = styled.div`
  display: flex;
  gap: 0.375rem;
`

const ActionButton = styled.button<{ $variant: 'accept' | 'reject' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'accept'
        ? 'rgba(16, 185, 129, 0.4)'
        : 'rgba(239, 68, 68, 0.4)'};
  background: ${({ $variant }) =>
    $variant === 'accept'
      ? 'rgba(16, 185, 129, 0.1)'
      : 'rgba(239, 68, 68, 0.1)'};
  color: ${({ $variant }) => ($variant === 'accept' ? '#10b981' : '#ef4444')};
  font-size: 0.7rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === 'accept'
        ? 'rgba(16, 185, 129, 0.2)'
        : 'rgba(239, 68, 68, 0.2)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }
`

const StatusBadge = styled.span<{ $status: CaseProposal['status'] }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.65rem;
  background: ${({ $status }) =>
    $status === 'accepted'
      ? 'rgba(16, 185, 129, 0.15)'
      : $status === 'rejected'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ theme, $status }) =>
    $status === 'accepted'
      ? '#10b981'
      : $status === 'rejected'
        ? '#ef4444'
        : theme.forecast.tertiaryText};

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }
`

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.warning};

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }
`

interface ProposalCardProps {
  proposalId: string
}

const SHORT_FIELDS: ProposableFieldPath[] = ['meta.title', 'workflow.severity']

function valueToString(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

const BLOCK_LEVEL_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
])

/**
 * HTML → plain text for diff display, preserving line breaks where
 * block-level elements appear. Sanitizes through DOMPurify first
 * (rejects <script>, event handlers, etc.) then walks the parsed
 * DOM rather than relying on a regex pass — regex-based tag
 * stripping is brittle against malformed HTML and is flagged by
 * static analysis as incomplete sanitization.
 *
 * The output is rendered as React text content (not
 * `dangerouslySetInnerHTML`), so any residual markup would be
 * auto-escaped at render time anyway. This is defense-in-depth on
 * top of that.
 */
function stripHtml(html: string): string {
  if (!html) return ''
  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    KEEP_CONTENT: true,
  })
  if (typeof document === 'undefined') {
    return sanitized
  }
  const doc = new DOMParser().parseFromString(sanitized, 'text/html')
  const out: string[] = []
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? '')
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as Element
    if (el.tagName === 'BR') {
      out.push('\n')
      return
    }
    for (const child of Array.from(el.childNodes)) walk(child)
    if (BLOCK_LEVEL_TAGS.has(el.tagName)) out.push('\n')
  }
  walk(doc.body)
  return out
    .join('')
    .replace(/\n\s*\n+/g, '\n\n')
    .trim()
}

export const ProposalCard: React.FC<ProposalCardProps> = ({ proposalId }) => {
  const queryBody = useMemo(
    () => ({
      ids: [proposalId],
      type: RolodexEntryType.CASE_PROPOSAL,
      limit: 1,
    }),
    [proposalId]
  )
  const { entries, isLoading } = useRolodexEntries(queryBody)
  const caseHandlers = useCaseUpdateHandlers()
  const { acceptProposal, rejectProposal } = useCaseProposalAPI()

  const [isApplying, setIsApplying] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [optimisticStatus, setOptimisticStatus] = useState<
    CaseProposal['status'] | null
  >(null)

  const proposal = useMemo<CaseProposal | null>(() => {
    if (!entries || entries.length === 0) return null
    const entry = entries[0]
    if (!entry?.raw) return null
    const data = entry.raw.data as Record<string, unknown> | undefined
    return parseCaseProposalEntry(proposalId, data)
  }, [entries, proposalId])

  if (isLoading && !proposal) {
    return (
      <Card $status='pending'>
        <Header>
          <Sparkles />
          Loading proposal…
        </Header>
      </Card>
    )
  }

  if (!proposal) {
    return null
  }

  const effectiveStatus = optimisticStatus ?? proposal.status
  const isPending = effectiveStatus === 'pending'
  const fieldLabel = FIELD_LABELS[proposal.fieldPath]
  const oldText = valueToString(proposal.currentValue)
  const newText = valueToString(proposal.proposedValue)

  const handleAccept = async () => {
    if (!isPending || isApplying) return
    setActionError(null)
    if (!caseHandlers) {
      setActionError(
        'Case context unavailable. Open this case directly to accept.'
      )
      return
    }
    setIsApplying(true)
    try {
      await acceptProposal(proposal, caseHandlers)
      setOptimisticStatus('accepted')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsApplying(false)
    }
  }

  const handleReject = async () => {
    if (!isPending || isApplying) return
    setActionError(null)
    setIsApplying(true)
    try {
      await rejectProposal(proposal.id)
      setOptimisticStatus('rejected')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsApplying(false)
    }
  }

  const isShortField = SHORT_FIELDS.includes(proposal.fieldPath)

  return (
    <Card $status={effectiveStatus}>
      <Header>
        <Sparkles />
        Suggested update
        <FieldLabel>· {fieldLabel}</FieldLabel>
        {effectiveStatus !== 'pending' && (
          <StatusBadge $status={effectiveStatus}>
            {effectiveStatus === 'accepted' && <CircleCheck />}
            {effectiveStatus === 'rejected' && <CircleX />}
            {effectiveStatus === 'superseded' && <CircleAlert />}
            {effectiveStatus}
          </StatusBadge>
        )}
      </Header>

      {isShortField ? (
        <PillRow>
          {oldText && <Pill $variant='old'>{oldText}</Pill>}
          {oldText && <Arrow>→</Arrow>}
          <Pill $variant='new'>{newText || '(empty)'}</Pill>
        </PillRow>
      ) : (
        <LineDiff oldText={stripHtml(oldText)} newText={stripHtml(newText)} />
      )}

      {proposal.rationale && <Rationale>{proposal.rationale}</Rationale>}

      {actionError && (
        <ErrorRow>
          <CircleAlert />
          {actionError}
        </ErrorRow>
      )}

      {isPending && (
        <Actions>
          <ActionButton
            type='button'
            $variant='accept'
            onClick={handleAccept}
            disabled={isApplying}>
            <Check />
            Accept
          </ActionButton>
          <ActionButton
            type='button'
            $variant='reject'
            onClick={handleReject}
            disabled={isApplying}>
            <X />
            Reject
          </ActionButton>
        </Actions>
      )}
    </Card>
  )
}
